'use client'

/**
 * The root poller (spec 2026-09-25-001 §5.5): every open tab asks
 * `changesSince` what changed in its firm, and hands each page to the platform
 * registry, which invalidates the query roots that read those tables.
 *
 * WHY HERE AND NOT IN `src/shared`. The registry is shared and holds no
 * business logic; the poller needs identity and the app's HTTP client, both
 * app-level, so it sits beside `OrgChangeWatcher` (research 09 §4.4).
 *
 * THE LOOP.
 * - Idle while identity names no firm, while the route's scope
 *   (`useCurrentPlatformScope`) is anything but `insurance-broker`, and while the identity in hand was resolved for another scope
 *   (the provider keeps the previous scope's identity as a placeholder while
 *   the new one loads). The root layout wraps `/cs` and `/underwriter` too,
 *   and neither is this poller's to serve (plan ruling 26).
 * - While the tab is visible, one poll every `POLL_INTERVAL_MS`, measured from
 *   the end of the last one, so polls never overlap. Paused while hidden; one
 *   poll at once when the tab is shown, focused or back online. Focus is
 *   ignored while polls are failing, so it cannot defeat the backoff; coming
 *   back online is the escape.
 * - A full page (`hasMore`) is followed by another poll at once, but only when
 *   the page moved a cursor, and at most `MAX_IMMEDIATE_REPOLLS` times in a
 *   row before the loop drops back to the ordinary beat.
 * - The cursor is held in memory for this tab only, never in browser storage,
 *   and belongs to the firm it was read for. A fresh tab, or a new firm, sends
 *   none and starts at the tail. A detour out of `insurance-broker` keeps it, so the tab
 *   catches up on return.
 * - Each poll names the tab's own recent writes by transaction id
 *   (`ownWriteXids`, noted from the BMS write answers' `x-cova-xid`), and the
 *   page's `echoed` tables, those only the tab's own writes changed, are left
 *   out of `apply`: the save already refetched them (query speed Q11). A table
 *   a co-worker also changed in the window is never echoed.
 * - For each page: `resync` invalidates everything through `applyResync` and
 *   adopts the returned tails; otherwise the page's tables go to `apply` in one
 *   call, which drops the access tables before `rootsFor` and throttles per
 *   (client, root). When a page names an access table, identity is refetched
 *   first and every other root on that page waits behind it (ruling 47). The
 *   cursor advances once the page has been applied, or after
 *   `APPLY_TIMEOUT_MS`: a refetch that hangs must not stall the poller. For an
 *   ordinary page the queries are already stale by then, since
 *   `invalidateQueries` marks them at once and only the refetch is awaited.
 *   For an access page whose identity refetch never settles, the waiting roots
 *   are never invalidated and the cursor moves on anyway; that is the accepted
 *   cost of never stalling. A run that was superseded (a new firm, a scope
 *   change, unmount) writes nothing.
 * - A writer in this tab may ask for a poll (`requestOwnWritePoll`) before it
 *   re-reads what it wrote. The next poll that starts after the ask carries
 *   it: that page is applied with the writer's roots left out, and the writer
 *   is answered once it is applied. A poll already out when the ask arrives
 *   may have started before the write, so it answers nobody and the next one
 *   goes at once. Hidden, failing, resynced, superseded or abandoned, the
 *   writer is answered `false` and re-reads anyway.
 * - A failed poll never throws out of the hook: it keeps its cursor and backs
 *   off, doubling the wait up to 16 intervals, and is reported once per run of
 *   failures. Unknown tables are counted and logged at debug level as counts.
 */
import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import type { ChangesCursor } from '@/contexts/audit/changes/domain/changes-page'
import { browserLogger } from '@/lib/browser-logger'
import { useIdentity } from '@/lib/providers/identity'
import {
  ACCESS_TABLES,
  apply,
  applyResync,
  ownWriteXids,
  serveOwnWritePolls,
  type QueryRoot,
} from '@/shared/lib/live-invalidation'
import { useCurrentPlatformScope } from '@/shared/scope'

import { fetchChangesPage } from './live-invalidation-client'

/** The poll interval, and the server's coalescing window (spec ruling 2). */
export const POLL_INTERVAL_MS = 3_000

/** A failing poll waits POLL_INTERVAL_MS times 2 to this power at most. */
const MAX_BACKOFF_DOUBLINGS = 4

/** How long a page's apply is awaited before the cursor advances anyway. */
export const APPLY_TIMEOUT_MS = 10_000

/** Consecutive `hasMore` re-polls allowed before the loop waits a beat. */
export const MAX_IMMEDIATE_REPOLLS = 20

/** Settle with `work`, or with undefined once `ms` has passed, whichever is first. */
const settleWithin = <A>(work: Promise<A>, ms: number): Promise<A | undefined> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), ms)
  })
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer))
}

/** True when `next` is past `sent` on either stream. A fresh tab sent nothing, so anything is progress. */
const movedPast = (next: ChangesCursor, sent: ChangesCursor | undefined): boolean =>
  sent === undefined || next.ledger > sent.ledger || next.changes > sent.changes

/** A writer waiting on a poll: the roots it will re-read, and how to answer it. */
interface Rider {
  readonly roots: ReadonlyArray<QueryRoot>
  abandoned: boolean
  readonly answer: (applied: boolean) => void
}

/**
 * Tell a writer to re-read by itself. It is marked abandoned first, so an
 * apply still running (one the timeout gave up on, or one an unmount left)
 * never leaves it anything when it decides later: it re-reads on its own.
 */
const release = (rider: Rider): void => {
  rider.abandoned = true
  rider.answer(false)
}

interface HeldCursor {
  readonly orgId: string
  readonly cursor: ChangesCursor
}

export function useLiveInvalidation(): void {
  const queryClient = useQueryClient()
  const { identity, identityScope } = useIdentity()
  const orgId = identity?.orgId ?? null
  const scope = useCurrentPlatformScope()
  const held = useRef<HeldCursor | null>(null)
  const unknownSeen = useRef(0)

  const firm = scope === 'insurance-broker' && identityScope === 'insurance-broker' ? orgId : null

  useEffect(() => {
    if (firm === null) return

    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    let inFlight = false
    let failures = 0
    let immediate = 0
    /** Writers waiting for a poll that starts after they asked. */
    let waiting: Array<Rider> = []
    /** Writers riding the poll that is out, answered by it unless the poller stops first. */
    let riding: ReadonlyArray<Rider> = []

    const visible = () => document.visibilityState === 'visible'

    const clear = () => {
      if (timer !== undefined) clearTimeout(timer)
      timer = undefined
    }

    const schedule = (delay: number) => {
      clear()
      if (controller.signal.aborted || !visible()) return
      timer = setTimeout(() => void poll(), delay)
    }

    const poll = async (): Promise<void> => {
      clear()
      if (inFlight || controller.signal.aborted || !visible()) return
      inFlight = true
      let delay = POLL_INTERVAL_MS
      let again = false
      let failed = false
      // The writers this poll answers: those who asked before it started.
      const riders = waiting
      waiting = []
      riding = riders
      let answered = false
      // ANSWERED AS SOON AS THE PAGE IS DECIDED, not once it is applied: the
      // apply awaits every other root's refetch, up to APPLY_TIMEOUT_MS, and the
      // writer only needs its roots left out (and identity refetched first when
      // the page names an access table) before it re-reads.
      let decided = false
      const decide = () => {
        if (decided) return
        decided = true
        for (const rider of riders) rider.answer(!rider.abandoned)
      }
      try {
        const sent = held.current?.orgId === firm ? held.current.cursor : undefined
        const page = await fetchChangesPage(sent, ownWriteXids(), controller.signal)
        if (controller.signal.aborted) return

        // An access table is never skipped, echoed or not (round 1
        // should-fix 9): a grant the tab's own save changed still refetches
        // identity and the access roots, which no save refreshes itself.
        const echoed = page.echoed ?? []
        const tables =
          echoed.length === 0 ? page.tables : page.tables.filter((table) => ACCESS_TABLES.has(table) || !echoed.includes(table))
        if (page.resync) {
          await settleWithin(applyResync(queryClient), APPLY_TIMEOUT_MS)
        } else if (tables.length > 0) {
          // Asked by `apply` when it decides, after identity on an access page,
          // not when the page arrives: a writer gone by then re-read under the
          // old grants, so its roots are this page's to apply. A writer that
          // asked while this poll was out is not answered by it, but its roots
          // are left out of it too: it re-reads after its own poll, which comes
          // after this page, so its re-read sees this page's changes, and this
          // page may well hold its write. Another user's change on this page to
          // those roots then waits for that re-read, at most the writer's 3 s.
          const rereadByWriter = () => [...riders, ...waiting].filter((r) => !r.abandoned).flatMap((r) => r.roots)
          const result = await settleWithin(apply(queryClient, tables, { rereadByWriter, onDecided: decide }), APPLY_TIMEOUT_MS)
          answered = result !== undefined
          const unknown = result?.unknownTables.length ?? 0
          if (unknown > 0) {
            unknownSeen.current += unknown
            console.debug('live-invalidation: unknown tables ignored', {
              count: unknown,
              total: unknownSeen.current,
            })
          }
        }

        // A superseded run must not write its firm's cursor over the new one.
        if (controller.signal.aborted) return
        // A page with nothing left to apply (empty, or only the tab's own
        // echoed writes) is applied as it arrives.
        if (!page.resync && tables.length === 0) answered = true

        held.current = { orgId: firm, cursor: page.cursor }
        failures = 0
        again = page.hasMore && movedPast(page.cursor, sent)
      } catch (error) {
        if (controller.signal.aborted) return
        failed = true
        failures += 1
        if (failures === 1) {
          browserLogger.captureException(error, { source: 'live-invalidation.poll' })
        }
        delay = POLL_INTERVAL_MS * 2 ** Math.min(failures, MAX_BACKOFF_DOUBLINGS)
      } finally {
        inFlight = false
        if (!decided) {
          for (const rider of riders) {
            if (answered && !rider.abandoned) rider.answer(true)
            else release(rider)
          }
        }
      }
      // After a failure the writers queued behind this poll re-read by
      // themselves and the backoff keeps its beat; after a success one more
      // poll goes at once only for a writer still waiting on it.
      if (failed) {
        for (const rider of waiting) release(rider)
        waiting = []
      } else {
        waiting = waiting.filter((rider) => !rider.abandoned)
      }
      if (waiting.length > 0) {
        void poll()
      } else if (again && immediate < MAX_IMMEDIATE_REPOLLS) {
        immediate += 1
        void poll()
      } else {
        immediate = 0
        schedule(delay)
      }
    }

    const ask = (roots: ReadonlyArray<QueryRoot>, signal?: AbortSignal): Promise<boolean> =>
      new Promise<boolean>((answer) => {
        // While polls are failing the writer is answered at once and the
        // backoff keeps its schedule: a write must not defeat it.
        if (controller.signal.aborted || !visible() || signal?.aborted || failures > 0) return answer(false)
        const rider: Rider = { roots, abandoned: false, answer }
        // A writer that stops waiting re-reads on its own, so it is answered at
        // once and the page it would have ridden is applied whole.
        signal?.addEventListener('abort', () => {
          rider.abandoned = true
          answer(false)
        })
        waiting.push(rider)
        if (!inFlight) void poll()
      })
    const stopServing = serveOwnWritePolls(queryClient, ask)
    // A writer is never left waiting on a poller that stopped: answered false,
    // it re-reads by itself. One already answered keeps its answer.
    const releaseWaiting = () => {
      for (const rider of waiting) release(rider)
      waiting = []
    }
    const releaseAll = () => {
      releaseWaiting()
      for (const rider of riding) release(rider)
      riding = []
    }

    const pollNow = () => void poll()
    const onFocus = () => {
      if (failures === 0) pollNow()
    }
    const onVisibility = () => {
      if (!visible()) {
        releaseWaiting()
        return clear()
      }
      // A tab shown again starts a fresh run of immediate re-polls.
      immediate = 0
      pollNow()
    }

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    window.addEventListener('online', pollNow)
    pollNow()

    return () => {
      controller.abort()
      clear()
      stopServing()
      releaseAll()
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('online', pollNow)
    }
  }, [firm, queryClient])
}

/**
 * The root layout's mount, beside `OrgChangeWatcher`: a null-rendering
 * watcher that runs the poller for the broker's QueryClient.
 */
export function LiveInvalidationWatcher(): null {
  useLiveInvalidation()
  return null
}
